import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonContent,
  IonIcon,
  IonRouterLink,
} from '@ionic/react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AppFooter } from './AppFooter';
import { setSeo, resetSeo, BASE_URL, type JsonLd } from '@/lib/seo';
import './StaticPage.css';

export type BpAccent = 'primary' | 'success' | 'warning' | 'info' | 'secondary' | 'danger';

const ACCENT_CYCLE: BpAccent[] = ['primary', 'success', 'warning', 'info', 'secondary', 'danger'];

export interface StaticSection {
  heading?: string;
  body: ReactNode;
  icon?: string;
  accent?: BpAccent;
}

interface StaticPageProps {
  title: string;
  intro?: ReactNode;
  heroIcon?: string;
  updated?: string;
  sections: StaticSection[];
  cta?: {
    title: string;
    body?: string;
    buttonLabel: string;
    routerLink: string;
    buttonIcon?: string;
  };
  /** Optional meta description override (derived from intro when omitted). */
  metaDescription?: string;
  /** Optional schema.org JSON-LD (e.g. Organization / ContactPoint). */
  jsonLd?: JsonLd | JsonLd[];
}

function toPlainText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toPlainText).join(' ');
  if (node && typeof node === 'object' && 'props' in (node as { props?: { children?: ReactNode } })) {
    return toPlainText((node as { props: { children?: ReactNode } }).props.children);
  }
  return '';
}

export function StaticPage({
  title,
  intro,
  heroIcon,
  updated,
  sections,
  cta,
  metaDescription,
  jsonLd,
}: StaticPageProps) {
  const location = useLocation();

  useEffect(() => {
    const derived = toPlainText(intro).replace(/\s+/g, ' ').trim();
    const description =
      metaDescription ?? (derived.length > 155 ? `${derived.slice(0, 152)}…` : derived);
    setSeo({
      title: `${title} | BluePages`,
      description,
      canonical: `${BASE_URL}${location.pathname}`,
      url: `${BASE_URL}${location.pathname}`,
      ...(jsonLd ? { jsonLd } : {}),
    });
    return () => resetSeo();
  }, [title, intro, location.pathname, metaDescription, jsonLd]);

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar style={{ '--background': 'transparent', '--border-width': '0' } as React.CSSProperties}>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" text="" mode="md" />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="bp-static-page">
          {/* Hero */}
          <div className="bp-static-hero">
            <div className="bp-static-hero-icon">
              <IonIcon icon={heroIcon ?? 'information-circle'} style={{ fontSize: '1.8rem', color: '#fff' }} />
            </div>
            <h1>{title}</h1>
            {intro && <p>{intro}</p>}
            {updated && <div className="bp-static-updated">Updated {updated}</div>}
          </div>

          {/* Sections */}
          <div className="bp-static-body">
            {sections.map((s, i) => {
              const accent = s.accent ?? ACCENT_CYCLE[i % ACCENT_CYCLE.length];
              return (
                <section key={i} className={`bp-static-section accent-${accent}`}>
                  {s.heading && (
                    <div className="bp-static-section-head">
                      {s.icon && (
                        <span className="bp-static-section-chip">
                          <IonIcon icon={s.icon} style={{ fontSize: '1.25rem' }} />
                        </span>
                      )}
                      <h2>{s.heading}</h2>
                    </div>
                  )}
                  <div className="bp-static-section-body">{s.body}</div>
                </section>
              );
            })}
          </div>

          {/* Footer CTA */}
          {cta && (
            <div className="bp-static-cta">
              <h3>{cta.title}</h3>
              {cta.body && <p>{cta.body}</p>}
              <IonRouterLink className="bp-cta-btn" routerLink={cta.routerLink}>
                {cta.buttonIcon && <IonIcon icon={cta.buttonIcon} />}
                {cta.buttonLabel}
              </IonRouterLink>
            </div>
          )}

          <AppFooter />
        </div>
      </IonContent>
    </IonPage>
  );
}
